import React from 'react';

import { Accordion } from 'shared/lib/SureDesignSystem';
import Footer from '../Footer';

import styles from './FAQ.module.css';

const FAQ = ({ location: { pathname } }) => {
  return (
    <div>
      <div className={styles.hero}>
        <h1 className={styles.hero_headline}>Frequently Asked Questions</h1>
        <p className={styles.hero_description}>
          Browse our FAQs to find info on Claims, Policies, Coverage, Payments,
          plus how-to guides for editing your account settings.
        </p>
      </div>

      <div className={styles.questions_container}>
        <div className={styles.questions_content}>
          <div className={styles.topics}>
            <h2 className={styles.topics_headline}>Browse Topics</h2>
            <ul className={styles.topics_linkList}>
              <li>
                <a
                  className={styles.topics_link}
                  href={`${pathname}#questions_about`}
                >
                  About SURE
                </a>
              </li>
              <li>
                <a className={styles.topics_link} href={`${pathname}#account`}>
                  Account
                </a>
              </li>
              <li>
                <a className={styles.topics_link} href={`${pathname}#policy`}>
                  Policy
                </a>
              </li>
              <li>
                <a className={styles.topics_link} href={`${pathname}#claims`}>
                  Claims
                </a>
              </li>
              <li>
                <a className={styles.topics_link} href={`${pathname}#coverage`}>
                  Coverage
                </a>
              </li>
              <li>
                <a className={styles.topics_link} href={`${pathname}#payment`}>
                  Payment
                </a>
              </li>
            </ul>
          </div>

          <div className={styles.questions_right}>
            <h2 id="questions_about" className={styles.questions_headline}>
              About SURE
            </h2>
            <Accordion
              className={styles.questions_block}
              items={[
                {
                  id: 1,
                  title: 'What is SURE?',
                  description:
                    'SURE offers dozens of insurance products including rideshare, renter’s, travel, smartphone, jewelry and more. Today, SURE provides the tools that power global insurance companies and consumer brands to distribute and service any insurance policy digitally. This cohesive ecosystem of managed technology, insurance products, APIs, and Software-as-a-Service helps insurance companies and brands manage digital sales, launch new markets, and modernize their systems.',
                },
                {
                  id: 3,
                  title: 'Is my information kept confidential?',
                  description:
                    'SURE uses reasonable administrative, physical, and technical safeguards to secure the personal information you share with us. Despite these safeguards and our additional efforts to secure your information, SURE cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third-parties will not be able to defeat our security, and improperly collect, access, steal, or modify your personal information.',
                },
                {
                  id: 4,
                  title: 'How do I contact SURE?',
                  description: (
                    <>
                      For questions about this privacy policy or about Sure’s
                      privacy and information security practices generally, you
                      can contact SURE at{' '}
                      <a href="mailto:info@sureapp.com">info@sureapp.com</a>.
                    </>
                  ),
                },
                {
                  id: 5,
                  title: 'How does SURE work?',
                  description:
                    'SURE enables you to purchase on-demand insurance and Episodic Insurance™. You can instantly purchase insurance without the need for quotes or any delay. SURE is the future of personal insurance.',
                },
                {
                  id: 6,
                  title: 'Where can SURE be used?',
                  description:
                    'You can use Sure from your smartphone or on the web.',
                },
                {
                  id: 7,
                  title: 'What is Episodic Insurance™?',
                  description:
                    'SURE has created Episodic insurance™ as a new type of on-demand insurance protection. It is delivered to you protection just-in-time, when you want it, and where you want it. Episodic Insurance™ allows you to make Smart Life Decisions™ in an instant.',
                },
              ]}
            />
            <h2 id="account" className={styles.questions_headline}>
              Account
            </h2>
            <Accordion
              className={styles.questions_block}
              items={[
                {
                  id: 1,
                  title: 'How do I update my account information?',
                  description:
                    "Open the SURE app, press the top left navigation button, and open Account Settings. When finished, tap 'Save'.",
                },
                {
                  id: 2,
                  title: 'How do I reset my password?',
                  description:
                    'From the Log In screen press the "Forgot Your Password" button, you will be directed to enter your email address to have a password reset sent to you.',
                },
                {
                  id: 3,
                  title: 'How do I reset a locked account?',
                  description:
                    'Contact the SURE Support Team and we can assist you in resetting your account.',
                },
                {
                  id: 4,
                  title: 'I am not receiving emails from SURE.',
                  description:
                    'Make sure you have the correct email address in your Account Settings and check your Spam Folders for any emails that might have been incorrectly classified as Spam. If problems persist contact the SURE Support Team.',
                },
                {
                  id: 5,
                  title: "Can I use SURE if I'm under 18?",
                  description:
                    'SURE is only available for customers 18 years and older.',
                },
              ]}
            />

            <h2 id="policy" className={styles.questions_headline}>
              Policy
            </h2>
            <Accordion
              className={styles.questions_block}
              items={[
                {
                  id: 1,
                  title: 'How do I know what my policy covers?',
                  description:
                    'A summary of your coverage and a sample policy is available to you when you sign up, and before you pay. You can edit your coverage, policy settings, such as the deductible, start dates, and more. After payment, we will instantly issue your final policy and send it to you by email.',
                },
                {
                  id: 2,
                  title: 'How do you calculate my premium?',
                  description:
                    'Premiums are calculated individually for each policyholder and are based on a number of different factors including your credit history, recent claims, and information about you or your property, business, etc.',
                },
                {
                  id: 3,
                  title: 'What policies does SURE offer?',
                  description:
                    'We offer Baggage Protection, Rental Car Insurance, Renters Insurance, Landlord Protection, Small Business Insurance, Collectibles Insurance, Fine Art Insurance, Jewelry & Watch Insurance, Wedding Insurance, Small Business Insurance, Flight Insurance and we are continuously adding more!',
                },
              ]}
            />

            <h2 id="claims" className={styles.questions_headline}>
              Claims
            </h2>
            <Accordion
              className={styles.questions_block}
              items={[
                {
                  id: 1,
                  title: 'How do I file a claim?',
                  description:
                    'Go to the Claims page on our website and click the "Submit a Claim" button. From there you will be guided through a short process.',
                },
                {
                  id: 2,
                  title: 'How will I get paid for a claim?',
                  description: 'You will receive a check in the mail.',
                },
              ]}
            />

            <h2 id="coverage" className={styles.questions_headline}>
              Coverage
            </h2>
            <Accordion
              className={styles.questions_block}
              items={[
                {
                  id: 1,
                  title: 'What is a deductible?',
                  description:
                    "The deductible is the amount of money on an insurance claim you will pay before the insurance company pays. It's what you'll be paying out of pocket prior to the coverage.",
                },
                {
                  id: 2,
                  title: 'What is liability protection?',
                  description:
                    'Liability protection provides protection against claims resulting from injuries and damage to people and/or property. It covers both legal costs and any legal payouts for which the insured would be responsible if found legally liable.',
                },
                {
                  id: 3,
                  title: 'What is a limit?',
                  description:
                    'An insurance limit is the maximum amount of money an insurance company will pay you for a covered loss. Usually, the higher your coverage limit, the higher your premium may be.',
                },
              ]}
            />

            <h2 id="payment" className={styles.questions_headline}>
              Payment
            </h2>
            <Accordion
              className={styles.questions_block}
              items={[
                {
                  id: 1,
                  title: 'What is the SURE chargeback policy?',
                  description: (
                    <>
                      <p>
                        <strong>What is a chargeback?</strong>
                      </p>
                      <p>
                        When a bank forcibly reverses a credit card transaction,
                        returning funds to the cardholder, it is known as a
                        chargeback. Chargebacks are usually performed when
                        fraudulent purchases have been made on a person’s credit
                        card. However, consumers may request a chargeback for
                        nearly any reason; as a result, it is possible to
                        accidentally commit "friendly fraud."
                      </p>
                      <p>
                        <strong>How does SURE handle chargebacks?</strong>
                      </p>
                      <p>
                        When SURE receives a chargeback notice, the policy which
                        was purchased is immediately cancelled, and all
                        associated services in the account are terminated. SURE
                        always considers chargebacks to be the result of fraud;
                        since fraudulent purchases go hand-in-hand with malware
                        and phishing, immediate termination of all services
                        related to the chargeback is the best way to protect the
                        public from harm.
                      </p>
                      <p>
                        <strong>How do I cancel a chargeback?</strong>
                      </p>
                      <p>
                        To undo a chargeback, you must contact your credit card
                        provider and issue a chargeback reversal. Reversing the
                        chargeback is the only way to restore a policy.
                        Chargeback reversal notices are often sent by postal
                        mail, which can result in days without coverage; when
                        contacting your bank, please ensure they fax a copy of
                        the paperwork us to allow us to restore everything as
                        quickly as possible. Our support team can be contacted
                        here and will be happy to help you resolve the issue.
                        Remember, the one and only way to restore a policy is to
                        reverse the chargeback. This returns the initial funds
                        to us, cancels the penalty fee we receive for “allowing”
                        fraud to be committed, and generally erases any "black
                        marks" against us for the transaction. We do not accept
                        additional payment for services on a different card.
                      </p>
                    </>
                  ),
                },
                {
                  id: 2,
                  title: 'What payment methods does SURE accept?',
                  description:
                    'We currently accept all major credit card & debit cards including Visa, Mastercard, American Express, and Discover through the SURE app. We also accept several platform specific payment methods like Apple Pay.',
                },
                {
                  id: 3,
                  title: 'How do I add a new payment method to my account?',
                  description:
                    'Open the SURE app, press the top left navigation button and open Account Settings, toggle to the Payment tab and press the Update Payment button. Credit and Debit cards may be added by manually entering the card number or automatically using the camera.',
                },
                {
                  id: 4,
                  title: 'How do I redeem a promotional code?',
                  description:
                    'Open the SURE app, press the top left navigation button and press Promotions. Enter your promotional code and press apply to have it automatically applied to your next purchase.',
                },
                {
                  id: 5,
                  title: 'How can I receive a refund?',
                  description: (
                    <>
                      <p>
                        If you want to cancel your policy, SURE makes it easy
                        with no cancellation fee. Just follow the steps below:
                      </p>
                      <p>
                        Call (844) 335-5441 to speak with a friendly agent or
                        submit a support request{' '}
                        <a
                          href="https://support.sureapp.com/hc/en-us/requests/new"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          here
                        </a>
                        .
                      </p>
                      <p>
                        You may be asked to say your SURE policy number, so
                        please have that ready.
                      </p>
                      <p>
                        You can cancel your policy at any time and in general
                        receive a refund for the remaining period you’ve paid
                        for.
                      </p>
                    </>
                  ),
                },
                {
                  id: 6,
                  title: 'How do I cancel a policy?',
                  description: (
                    <>
                      <p>
                        If you want to cancel your policy, SURE makes it easy
                        with no cancellation fee. Just follow the steps below:
                      </p>
                      <ol>
                        <li>
                          Call (844) 335-5441 to speak with a friendly, licensed
                          agent.
                        </li>
                        <li>
                          You may be asked to provide your SURE policy number,
                          so please have that ready.
                        </li>
                        <li>
                          Before you cancel your policy, there are a few things
                          you should consider to be sure it's the best next
                          step.
                        </li>
                      </ol>
                      <h2>You should consider keeping your SURE policy if:</h2>
                      <p>
                        <strong>
                          Things have changed in your life, like...
                        </strong>
                      </p>
                      <ul>
                        <li>
                          You've moved, even to another state.
                          <br />
                          Your policy can simply go with you. We offer insurance
                          in all 50 states and in DC. Get a quick quote online
                          for the state you are moving to by visiting{' '}
                          <a href="https://www.sureapp.com/">
                            https://www.sureapp.com/
                          </a>
                        </li>
                        <li>
                          You had a change in marital status.
                          <br />
                          Just add or remove your spouse or partner. By doing
                          either, you could qualify for additional discounts.
                          You can conveniently make changes online or in your
                          SURE Mobile App.
                        </li>
                      </ul>
                      <p>
                        <strong>
                          You want to save money or simplify your insurance,
                          because...
                        </strong>
                      </p>
                      <ul>
                        <li>
                          You want other types of policies with one company.
                          <br />
                          You can easily get a quote for homeowners, renters,
                          and many other types of insurance through SURE.By
                          combining all of your coverages, you may even get
                          additional discounts!
                        </li>
                        <li>
                          You can't afford the insurance.
                          <br />
                          It's possible to pay less for your insurance by
                          adjusting your coverage. You can also log in to your
                          account online or through the SURE Mobile App and edit
                          your coverage.
                        </li>
                        <li>
                          You found a cheaper rate somewhere else.
                          <br />A lower rate could mean a compromise in the
                          quality of your coverage. Make sure you're comparing
                          identical policies from another company—one that has
                          the same coverage, deductibles, and benefits that you
                          have now.
                        </li>
                      </ul>
                      <p>
                        Do any of these reasons apply to you? Just call (844)
                        335-5441 so we can help manage your policy to fit your
                        needs.
                      </p>
                      <p>We look forward to helping you.</p>
                    </>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQ;
